import { useState } from "react";
import { Link } from "react-router-dom";

import { Loading } from "../components/StateViews";
import { trpc } from "../trpc";

export function DohasPage() {
  const me = trpc.session.me.useQuery();
  const mandals = trpc.dohas.mandals.useQuery();
  const [mandal, setMandal] = useState<number | null>(null);
  const [digitised, setDigitised] = useState<boolean | null>(null);

  const list = trpc.dohas.list.useInfiniteQuery(
    {
      mandal: mandal ?? undefined,
      digitised: digitised ?? undefined,
      limit: 120,
    },
    { getNextPageParam: (last) => last.nextCursor ?? undefined },
  );
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];

  // Which dohas are already in my study — so their cards read differently.
  const studySlugs = trpc.study.slugs.useQuery(undefined, {
    enabled: me.data?.approved === true,
  });
  const following = new Set(studySlugs.data ?? []);

  return (
    <div className="mx-auto max-w-6xl px-5 pt-14 sm:px-8">
      <p className="eyebrow">The dohas</p>
      <h1 className="mt-3 font-serif text-4xl text-ink">Walk the couplets</h1>

      <div className="mt-8 flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => setMandal(null)}
          className={pillClass(mandal === null)}
        >
          All mandals
        </button>
        {mandals.data?.map((m) => (
          <button
            key={m.mandal}
            type="button"
            onClick={() => setMandal(m.mandal)}
            className={pillClass(mandal === m.mandal)}
          >
            Mandal {m.mandal}
            <span className="ml-1.5 text-ink-faint/70">{m.count}</span>
          </button>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <span className="eyebrow mr-1">Show</span>
        <button
          type="button"
          onClick={() => setDigitised(null)}
          className={pillClass(digitised === null)}
        >
          All
        </button>
        <button
          type="button"
          onClick={() => setDigitised(true)}
          className={pillClass(digitised === true)}
        >
          Digitised
        </button>
        <button
          type="button"
          onClick={() => setDigitised(false)}
          className={pillClass(digitised === false)}
        >
          Not digitised
        </button>
      </div>

      {list.isLoading ? (
        <Loading label="Gathering dohas" />
      ) : items.length === 0 ? (
        <p className="mt-10 text-sm text-ink-faint">
          {digitised === true
            ? "None yet — capture a doha or add its verse to see it here."
            : "No dohas match this filter."}
        </p>
      ) : (
        <>
          <ul className="mt-8 grid grid-cols-2 gap-1.5 sm:grid-cols-4 lg:grid-cols-6">
            {items.map((d) => {
              const inStudy = following.has(d.slug);
              return (
                <li key={d.slug}>
                  <Link
                    to={`/doha/${d.slug}`}
                    title={inStudy ? "In your study" : undefined}
                    className={[
                      "relative block rounded-lg border px-3 py-2.5 text-center font-mono text-sm transition-all hover:-translate-y-0.5 hover:border-terracotta/50 hover:text-terracotta",
                      inStudy
                        ? "border-terracotta/40 bg-terracotta-wash text-terracotta"
                        : "border-line bg-paper-raised text-ink-soft",
                    ].join(" ")}
                  >
                    {d.digitised ? (
                      <span
                        className={[
                          "absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full",
                          inStudy ? "bg-terracotta/70" : "bg-terracotta",
                        ].join(" ")}
                        title="Digitised"
                      />
                    ) : null}
                    {d.slug}
                  </Link>
                </li>
              );
            })}
          </ul>

          {list.hasNextPage ? (
            <div className="mt-10 flex justify-center">
              <button
                type="button"
                onClick={() => void list.fetchNextPage()}
                disabled={list.isFetchingNextPage}
                className="rounded-full border border-line-strong px-6 py-2 text-sm font-medium text-ink transition-colors hover:bg-paper-sunk disabled:opacity-50"
              >
                {list.isFetchingNextPage ? "Loading…" : "More dohas"}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function pillClass(active: boolean): string {
  return [
    "rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors",
    active
      ? "bg-ink text-paper"
      : "text-ink-faint hover:bg-paper-sunk hover:text-ink",
  ].join(" ");
}
